/**
 * `/casos/:id/certificados` (E157.F3, GUS-157, ADR-0107 §2.7): a tela "Certificados e carimbo" na
 * área da equipe, sobre a visão composta no servidor. "Voltar à revisão" leva à pré-análise
 * eletrônica; "Rever leitura de um campo" leva à ficha da leitura (E155, ADR-0105 §2.7), onde o
 * cartão "Rever leitura na fonte" vive. O link de entrada sai da pré-análise eletrônica.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { CertificadosECarimbo } from '@/components/CertificadosECarimbo/CertificadosECarimbo';
import { ProximoPasso } from '@/components/ProximoPasso/ProximoPasso';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  CertificadosECarimboError,
  lerCertificadosECarimbo,
  type CertificadosECarimboData,
} from '@/lib/certificados_e_carimbo';

const t = ptBR.certificadosECarimbo;

interface Falha {
  texto: string;
  retentavel: boolean;
}

function falhaDe(err: unknown): Falha {
  const motivo = err instanceof CertificadosECarimboError ? err.motivo : 'indisponivel';
  if (motivo === 'sem_permissao') return { texto: t.semPermissao, retentavel: false };
  if (motivo === 'nao_encontrado') return { texto: t.naoEncontrado, retentavel: false };
  return { texto: t.erro, retentavel: true };
}

export function CertificadosECarimboPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const [visao, setVisao] = useState<CertificadosECarimboData | null>(null);
  const [falha, setFalha] = useState<Falha | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setVisao(await lerCertificadosECarimbo(client, caseId));
      setFalha(null);
    } catch (err) {
      setFalha(falhaDe(err));
    }
  }, [client, caseId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <section
      className="mx-auto flex max-w-5xl flex-col gap-sc-6 p-sc-6"
      data-testid="certificados-page"
    >
      <header>
        <h1 className="text-display-sm text-fg">{t.titulo}</h1>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.subtitulo}</p>
        <nav className="mt-sc-2 flex flex-wrap gap-sc-4 text-body-sm" aria-label={t.titulo}>
          <Link
            className="text-accent-700 underline-offset-4 hover:underline"
            to={`/casos/${caseId}/pre-analise`}
            data-testid="certificados-voltar"
          >
            {t.voltar}
          </Link>
          <Link
            className="text-accent-700 underline-offset-4 hover:underline"
            to={`/casos/${caseId}/informacoes-da-leitura#ficha-da-leitura`}
            data-testid="certificados-rever-leitura"
          >
            {t.reverLeitura}
          </Link>
        </nav>
      </header>
      <ProximoPasso client={client} caseId={caseId} />
      {falha !== null ? (
        <div role="alert" className="space-y-sc-2" data-testid="certificados-erro">
          <p className="text-body-sm text-danger-700">{falha.texto}</p>
          {falha.retentavel ? (
            <Button type="button" variant="secondary" size="sm" onClick={() => void carregar()}>
              {t.tentarDeNovo}
            </Button>
          ) : null}
        </div>
      ) : visao === null ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : (
        <CertificadosECarimbo visao={visao} />
      )}
    </section>
  );
}
